import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { Fragment, useMemo, type ReactNode, type Ref } from "react";
import { usePickerRoot } from "./PickerContext";
import { PickerFlyout } from "./PickerFlyout";
import { usePickerAutoOpenValue } from "./usePickerAutoOpenValue";
import { usePerKeyCallback } from "./usePickerStable";
import { PickerItem } from "./PickerItem";
import { PickerLoadMore } from "./PickerLoadMore";
import {
    PickerEmpty,
    PickerError,
    PickerFooter,
    PickerLoading,
    PickerSearch,
} from "./PickerParts";
import { PickerInlineSurface, PickerList, PickerPopup } from "./PickerPopup";
import {
    PickerSection,
    PickerSectionTitle,
    PickerSeparator,
} from "./PickerSection";
import type { PickerRowGroup, PickerTreeRow } from "./PickerTree";
import type {
    PickerEmptyBehavior,
    PickerItemValue,
    PickerNested,
    PickerPopupProps,
    PickerStatus,
} from "./Picker.types";

/** The one place the "No results" copy lives; every root and level defaults to it. */
export const PICKER_DEFAULT_EMPTY_LABEL = "No results";

export interface PickerListContentProps<T extends PickerItemValue> {
    groups: ReadonlyArray<PickerRowGroup<T>>;
    /** The filtering text; a query puts tree rows into their flat search layout. */
    query: string;
    /** The root's trigger element, rendered before the surface. */
    trigger?: ReactNode;
    /** Whether the search takes focus on mount; omitted, `Picker.Search`'s own default (off for auto-opened levels). */
    searchAutoFocus?: boolean;
    /** Reaches the rendered search `<input>`. */
    searchRef?: Ref<HTMLInputElement>;
    hasPopupSearch: boolean;
    isInline: boolean;
    /** Presentation of rows with children; flyout rows open their own level. */
    nested?: PickerNested;
    /** Placement and pointer hooks of the popup surface (flyout levels, the attached preset). */
    popupProps?: Partial<PickerPopupProps>;
    /** Rendered at the top of the surface, above the search and the list. */
    header?: ReactNode;
    /** Id of the visible heading that names the listbox. */
    listLabelledBy?: string;
    searchPlaceholder?: string;
    status?: PickerStatus;
    loadingLabel?: string;
    errorLabel?: string;
    /** Copy of the empty row; defaults to `PICKER_DEFAULT_EMPTY_LABEL`. */
    emptyLabel?: string;
    emptyBehavior?: PickerEmptyBehavior;
    onExpandedChange: (value: T, expanded: boolean) => void;
    footer?: ReactNode;
    children?: ReactNode;
}

const PickerRows = <T extends PickerItemValue>({
    rows,
    hasQuery,
    isFirstGroup,
    nested,
    autoOpenValue,
    expandedChangeFor,
    toggleExpandedFor,
}: {
    rows: ReadonlyArray<PickerTreeRow<T>>;
    hasQuery: boolean;
    isFirstGroup: boolean;
    nested: PickerNested;
    /** The flyout row the top query opens by itself, if any. */
    autoOpenValue: PickerItemValue | undefined;
    /** Stable per-value handlers, so memoised rows keep their props. */
    expandedChangeFor: (value: T) => (expanded: boolean) => void;
    toggleExpandedFor: (value: T) => () => void;
}) => {
    const root = usePickerRoot();

    return rows.map((row, index) => {
        const item = (
            <PickerItem
                key={row.item.value}
                value={row.item.value}
                leading={row.item.leading}
                labelHint={row.item.labelHint}
                wrap={row.item.wrap}
                description={row.item.description}
                rightText={row.item.rightText}
                disabled={row.item.disabled}
                disabledReason={row.item.disabledReason}
                tooltip={row.item.tooltip}
                destructive={row.item.destructive}
                depth={row.depth}
                treePosition={row.treePosition}
                ancestorLines={row.ancestorLines}
                expandable={row.isParent && !row.isFlyout && !hasQuery}
                expanded={row.isExpanded}
                onExpandedChange={expandedChangeFor(row.item.value)}
                onSelect={
                    row.isSelectable || row.isFlyout
                        ? undefined
                        : toggleExpandedFor(row.item.value)
                }
                closeOnSelect={false}
                data-forced-state={
                    isFirstGroup && index === 0 ? root.forcedState : undefined
                }
            >
                {row.item.content ?? row.item.label}
            </PickerItem>
        );
        if (!row.isFlyout) {
            return item;
        }
        return (
            <PickerFlyout
                key={row.item.value}
                row={item}
                items={row.item.children}
                sections={row.item.sections}
                nested={nested}
                isAutoOpenTarget={row.item.value === autoOpenValue}
                {...row.item.flyout}
            />
        );
    });
};

/**
 * Whether the empty row shows, and whether the footer needs its divider. A
 * page that matched nothing is not "No results" while more pages exist; with
 * nothing above it (hidden empty row, no match) the footer stands alone.
 */
const resolveStatusRows = ({
    status,
    emptyBehavior,
    hasVisibleRows,
    hasMore,
    loadMoreStatus,
}: {
    status: PickerStatus;
    emptyBehavior: PickerEmptyBehavior;
    hasVisibleRows: boolean;
    hasMore: boolean;
    loadMoreStatus: PickerStatus;
}) => {
    const isIdle = status === "idle";
    const isLoadMoreIdle = loadMoreStatus === "idle";
    const shouldRenderEmpty =
        isIdle &&
        emptyBehavior === "show" &&
        !hasVisibleRows &&
        !hasMore &&
        isLoadMoreIdle;
    return {
        shouldRenderEmpty,
        hasRowsAboveFooter:
            !isIdle || hasVisibleRows || shouldRenderEmpty || !isLoadMoreIdle,
    };
};

/**
 * The popup body every data-driven root renders from `items` or `sections`:
 * flattened tree rows (grouped under section titles with separators between
 * groups), the optional popup search bar, status rows (loading / error /
 * empty), the load-more end of a paged list and the creatable footer. Extra
 * `children` land after the list.
 */
export const PickerListContent = <T extends PickerItemValue>({
    groups,
    query,
    trigger,
    hasPopupSearch,
    searchAutoFocus,
    searchRef,
    isInline,
    nested = "tree",
    popupProps,
    header,
    listLabelledBy,
    searchPlaceholder,
    status = "idle",
    loadingLabel,
    errorLabel,
    emptyLabel = PICKER_DEFAULT_EMPTY_LABEL,
    emptyBehavior = "show",
    onExpandedChange,
    footer,
    children,
}: PickerListContentProps<T>) => {
    const hasQuery = query.trim() !== "";
    const isIdle = status === "idle";
    const visibleGroups = groups.filter((group) => group.rows.length > 0);
    const autoOpenValue = usePickerAutoOpenValue(visibleGroups);
    const expandedChangeFor = usePerKeyCallback(onExpandedChange);
    const expandedByValue = useMemo(
        () =>
            new Map(
                groups.flatMap((group) =>
                    group.rows.map((row) => [row.item.value, row.isExpanded]),
                ),
            ),
        [groups],
    );
    const expandedByValueRef = useLatestRef(expandedByValue);
    const toggleExpandedFor = usePerKeyCallback((value: T) =>
        onExpandedChange(value, expandedByValueRef.current.get(value) !== true),
    );
    const { hasMore, loadMoreStatus } = usePickerRoot();
    const { shouldRenderEmpty, hasRowsAboveFooter } = resolveStatusRows({
        status,
        emptyBehavior,
        hasVisibleRows: visibleGroups.length > 0,
        hasMore,
        loadMoreStatus,
    });
    const renderSurface = (body: ReactNode) =>
        isInline ? (
            <PickerInlineSurface className={popupProps?.className}>
                {body}
            </PickerInlineSurface>
        ) : (
            <PickerPopup {...popupProps}>{body}</PickerPopup>
        );

    return (
        <>
            {trigger}
            {renderSurface(
                <>
                    {header}
                    {hasPopupSearch && (
                        <PickerSearch
                            placeholder={searchPlaceholder}
                            autoFocus={searchAutoFocus}
                            ref={searchRef}
                        />
                    )}
                    <PickerList aria-labelledby={listLabelledBy}>
                        {status === "loading" && (
                            <PickerLoading>{loadingLabel}</PickerLoading>
                        )}
                        {status === "error" && (
                            <PickerError>{errorLabel}</PickerError>
                        )}
                        {isIdle &&
                            visibleGroups.map((group, groupIndex) => {
                                const rows = (
                                    <PickerRows
                                        rows={group.rows}
                                        hasQuery={hasQuery}
                                        isFirstGroup={groupIndex === 0}
                                        nested={nested}
                                        autoOpenValue={autoOpenValue}
                                        expandedChangeFor={expandedChangeFor}
                                        toggleExpandedFor={toggleExpandedFor}
                                    />
                                );
                                if (group.section === undefined) {
                                    return (
                                        <Fragment key={group.key}>
                                            {rows}
                                        </Fragment>
                                    );
                                }
                                return (
                                    <Fragment key={group.key}>
                                        {groupIndex > 0 && <PickerSeparator />}
                                        <PickerSection>
                                            {group.section.title !==
                                                undefined && (
                                                <PickerSectionTitle
                                                    icon={group.section.icon}
                                                    selectAll={
                                                        group.section.selectAll
                                                            ? {
                                                                  items: group
                                                                      .section
                                                                      .items,
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    {group.section.title}
                                                </PickerSectionTitle>
                                            )}
                                            {rows}
                                        </PickerSection>
                                    </Fragment>
                                );
                            })}
                        {shouldRenderEmpty && (
                            <PickerEmpty>{emptyLabel}</PickerEmpty>
                        )}
                        {isIdle && <PickerLoadMore />}
                        {footer !== undefined && footer !== null && (
                            <PickerFooter separator={hasRowsAboveFooter}>
                                {footer}
                            </PickerFooter>
                        )}
                    </PickerList>
                    {children}
                </>,
            )}
        </>
    );
};
